import { ConfirmDialog } from "@connected-repo/ui-mui/components/ConfirmDialog";
import { Typography } from "@connected-repo/ui-mui/data-display/Typography";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { TextField } from "@connected-repo/ui-mui/form/TextField";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { Card } from "@connected-repo/ui-mui/layout/Card";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import type { SuperAdminTeamDetail } from "@connected-repo/zod-schemas/minute_ledger.zod";
import { TEAM_ROOM_CAPACITY_MAX } from "@connected-repo/zod-schemas/team_app.zod";
import { orpc } from "@superadmin/utils/orpc.client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";

/**
 * How many rooms at the stands the team may run at once (root ADR-036),
 * assigned here with the minutes and never by the team. The capacity is
 * shared evenly between the team's active exhibitors, the rest borrowed;
 * 0 switches rooms off. Lowering it ends nothing already open, but a stand
 * past its new share cannot start another until rooms free up.
 */
export function RoomCapacityCard({
	teamId,
	rooms,
}: {
	teamId: string;
	rooms: SuperAdminTeamDetail["rooms"];
}) {
	const queryClient = useQueryClient();
	const [value, setValue] = useState(String(rooms.capacity));
	const [asking, setAsking] = useState(false);
	const mutation = useMutation(
		orpc.setTeamRoomCapacity.mutationOptions({
			onSuccess: async (team) => {
				toast.success(
					team.roomCapacity > 0 ? `Room capacity set to ${team.roomCapacity}` : "Rooms switched off",
				);
				setValue(String(team.roomCapacity));
				await queryClient.invalidateQueries({ queryKey: orpc.getTeam.key() });
				await queryClient.invalidateQueries({ queryKey: orpc.listTeams.key() });
			},
			onError: (err: Error) => toast.error(err.message),
		}),
	);
	const parsed = /^\d+$/.test(value.trim()) ? Number(value.trim()) : Number.NaN;
	const valid = Number.isInteger(parsed) && parsed >= 0 && parsed <= TEAM_ROOM_CAPACITY_MAX;
	const unchanged = valid && parsed === rooms.capacity;
	const save = () => mutation.mutate({ teamId, roomCapacity: parsed });

	const figures: [string, number][] = [
		["Capacity", rooms.capacity],
		["Active exhibitors", rooms.activeExhibitors],
		["Dedicated each", rooms.dedicatedEach],
		["Open now", rooms.open],
		["Free", rooms.free],
	];

	return (
		<Card sx={{ p: 2.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
			<Stack spacing={1.5}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						Rooms at the stands
					</Typography>
					<Typography variant="body2" color="text.secondary">
						How many one-to-one conversations the team's exhibitors may hold at once. Each active
						exhibitor is held an even share; past that they borrow what is free. Rooms bill by the
						minute from the same balance. 0 switches rooms off.
					</Typography>
				</Box>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", sm: "repeat(5, minmax(0,1fr))" },
						gap: 1.5,
					}}
				>
					{figures.map(([label, n]) => (
						<Box key={label}>
							<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
								{label}
							</Typography>
							<Typography sx={{ fontWeight: 800, fontSize: 20 }}>{n}</Typography>
						</Box>
					))}
				</Box>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
					<TextField
						label="Room capacity"
						type="number"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						error={!valid}
						helperText={`0–${TEAM_ROOM_CAPACITY_MAX} rooms at once.`}
						fullWidth
						disabled={mutation.isPending}
						slotProps={{ htmlInput: { min: 0, max: TEAM_ROOM_CAPACITY_MAX, step: 1, inputMode: "numeric" } }}
					/>
					<Button
						variant="contained"
						disabled={!valid || unchanged || mutation.isPending}
						// A first assignment changes nothing running; lowering or withdrawing one does.
						onClick={() => (rooms.capacity > 0 ? setAsking(true) : save())}
						sx={{ minHeight: 44, borderRadius: 2, whiteSpace: "nowrap" }}
					>
						{mutation.isPending ? "Saving…" : "Save"}
					</Button>
				</Stack>
			</Stack>
			<ConfirmDialog
				open={asking}
				title={parsed === 0 ? "Switch rooms off?" : `Change room capacity to ${parsed}?`}
				description={
					parsed === 0
						? `No exhibitor can start a room until capacity is given back.${rooms.open > 0 ? ` The ${rooms.open} open now run on to their end.` : ""}`
						: `Each active exhibitor's share is worked out again from ${parsed}.${parsed < rooms.open ? ` ${rooms.open} are open now; they run on, and no new room starts until fewer than ${parsed} are open.` : ""}`
				}
				confirmLabel={parsed === 0 ? "Switch off" : "Change"}
				pending={mutation.isPending}
				onClose={() => setAsking(false)}
				onConfirm={() => {
					setAsking(false);
					save();
				}}
			/>
		</Card>
	);
}
